import { arrayToObject, EmptyArray } from 'lib/array/arrayUtils'
import { filterObject, getAttribute, mapObject, omitKeys } from 'lib/object/objectUtils'

export const initialFormState: App.Checkout.FormState = {
  travellerForms: [],
  commsResubscribe: undefined,
  deliveryForm: {},
  flightSeatSelections: {},
}

const schemaDeref = (fieldSchema: App.Checkout.FormFieldSchema, formSchema: App.Checkout.FormSchema) => {
  if ('$ref' in fieldSchema && fieldSchema.$ref) {
    const parts = fieldSchema.$ref.split('/')
    const path = parts.slice(1)
    return getAttribute(formSchema, path)
  } else {
    return fieldSchema
  }
}

export function flattenFormFields(properties: App.Checkout.TravellerFormSchema['properties'], formSchema: App.Checkout.FormSchema): App.Checkout.TravellerFormSchema['properties'] {
  let flattened = mapObject(properties, property => schemaDeref(property, formSchema))
  for (const key in properties) {
    const fieldSchema = flattened[key]
    if (fieldSchema.subFields) {
      flattened = {
        ...omitKeys([key], flattened),
        ...fieldSchema.subFields,
      }
    }
  }
  return flattened
}

export function flattenRequiredArray(schema: App.Checkout.TravellerFormSchema, formSchema: App.Checkout.FormSchema): Array<string> {
  let flattened = schema.required
  if (!flattened || flattened.length === 0) {
    return EmptyArray
  }
  for (const key in schema.properties) {
    const fieldSchema = schemaDeref(schema.properties[key], formSchema)
    if (flattened.includes(key) && 'subFieldsRequired' in fieldSchema && Array.isArray(fieldSchema.subFieldsRequired)) {
      flattened = [
        ...flattened.filter(k => k !== key),
        ...fieldSchema.subFieldsRequired,
      ]
    }
  }
  return flattened
}

export function removeUnknownFields(
  schema: App.Checkout.FormSchema,
  form: App.Checkout.TravellerForm,
): App.Checkout.TravellerForm {
  const fields = new Set(Object.keys(flattenFormFields(schema.properties[form.id].properties, schema)))
  const formData = filterObject(form.data, (key: keyof App.Checkout.TravellerFormData) => (
    // Temporary until old phone field is removed from schemas
    key === 'phone' ||
    // Not sure why this cast is needed in strict mode... keyof App.Checkout.TravellerForm should be `string`,
    // but Typescript thinks it's `string | number`??
    fields.has(key as string)
  )) as App.Checkout.TravellerFormData
  return {
    ...form,
    data: formData,
  }
}

export function removeUnknownTravellersAndFields(
  schema: App.Checkout.FormSchema,
  forms: App.Checkout.FormState['travellerForms'],
): App.Checkout.FormState['travellerForms'] {
  const schemaTravellers = new Set(Object.keys(schema.properties))
  if (schema) {
    return forms
      .filter(form => schemaTravellers.has(form.id))
      .map(form => removeUnknownFields(schema, form))
  }
  return forms
}

function filterRequiredByDependencies(
  required: Array<string>,
  properties: App.Checkout.TravellerFormSchema['properties'],
  formSchema: App.Checkout.FormSchema,
  formData: App.Checkout.TravellerFormData | undefined,
): Array<string> {
  const flatProperties = flattenFormFields(properties, formSchema)
  return required.filter(field => {
    const dependsOn = flatProperties[field]?.dependsOn
    if (!dependsOn) return true
    const parentValue = formData?.[dependsOn.fieldKey]
    return Array.isArray(parentValue)
      ? parentValue.includes(dependsOn.value)
      : parentValue === dependsOn.value
  })
}

/** Checks that required fields are present and non-empty (trims whitespace for strings). Does NOT validate format/correctness of values. */
export function isCompleted(
  formSchema: App.Checkout.FormSchema,
  travellerId: string,
  travellerData: App.Checkout.TravellerForm | undefined,
) {
  let required = flattenRequiredArray(formSchema.properties[travellerId], formSchema)

  required = filterRequiredByDependencies(required, formSchema.properties[travellerId].properties, formSchema, travellerData?.data)

  if (required.length === 0) {
    return true
  }

  const schemaComplete = required.every(field => {
    const value = travellerData?.data[field]
    if (typeof value === 'string') return !!value.trim()
    return !!value
  })
  if (!schemaComplete) return false

  // Gender is conditionally required for gender-ambiguous titles (e.g. "Dr")
  // but isn't in the schema's required array since it uses frontend-driven visibility
  if (travellerData?.data.title === 'Dr' && !travellerData?.data.gender?.trim()) return false

  return true
}

/** Checks that all traveller forms have their required fields present and non-empty — does NOT validate format/correctness of values. */
export function allFormsCompleted(
  formSchema: App.Checkout.FormSchema,
  travellerForms: Array<App.Checkout.TravellerForm>,
): boolean {
  const formsById = arrayToObject(travellerForms, t => t.id)
  return Object.keys(formSchema.properties).every(travellerId => isCompleted(formSchema, travellerId, formsById[travellerId]))
}

export function isE2EUser(userEmail: string) {
  return userEmail.match(/@luxuryescapesautomatede2edomain\.com$/)
}
